// Conflits de synchronisation côté agent (R9) : lecture des deux versions, celle de l'appareil
// (opération conservée telle quelle) et celle du serveur (motif et version), sans action : la
// décision appartient au responsable technique (D-041).
import { EtatVide, LienBouton, Squelette } from '@cc/ui';
import { CircleCheck } from 'lucide-react';
import { dateHeure, resumeCharge } from '../../terrain/etat-local';
import { useOperationsLocales, usePhotosLocales } from '../../terrain/useTerrain';
import { Definitions, EnTeteTerrain, Mono, TexteEtat } from './commun';
import styles from './terrain.module.css';

export function PageConflits() {
  const operations = useOperationsLocales();
  const photos = usePhotosLocales();
  if (operations.chargement) return <Squelette lignes={5} libelle="Lecture des conflits sur l'appareil" />;
  const conflits = (operations.donnees ?? []).filter((o) => o.etat === 'conflit');
  const rejets = (operations.donnees ?? []).filter((o) => o.etat === 'rejetee');
  return (
    <>
      <EnTeteTerrain titre="Conflits" sousTitre="Une opération enregistrée sur cet appareil vise un objet que le serveur a changé entre-temps. Les deux versions sont conservées. Consultez la marche à suivre indiquée pour chaque conflit." />
      {conflits.length === 0 ? (
        <EtatVide titre="Aucun conflit" icone={CircleCheck} niveau={2}>
          <p>Toutes les opérations synchronisées ont été appliquées ou ignorées comme doublons.</p>
        </EtatVide>
      ) : (
        <div className={styles.pile}>
          {conflits.map((o) => {
            const siennes = (photos.donnees ?? []).filter((p) => p.operation_id_client === o.id_client);
            return (
              <article key={o.id_client} className={styles.carte} aria-labelledby={`conflit-${o.id_client}`}>
                <h2 id={`conflit-${o.id_client}`} className={styles.blocTitre}>
                  {o.titre} · {o.objet_libelle}
                </h2>
                <TexteEtat libelle={o.objet_type === 'emplacement' ? 'Conflit : position ou occupant à revérifier' : 'Conflit : en attente de la décision du responsable'} ton="attention" />
                <section aria-label="Votre version, enregistrée sur cet appareil" className={styles.bloc}>
                  <span className={styles.blocEtiquette}>Votre version (cet appareil)</span>
                  <Definitions
                    elements={[
                      { terme: 'Enregistrée le', valeur: dateHeure(o.enregistre_le) },
                      { terme: 'Version attendue', valeur: <Mono>{o.version_attendue}</Mono> },
                      { terme: 'Photos', valeur: siennes.length ? `${siennes.length} (${siennes.filter((p) => p.etat === 'envoyee').length} envoyée(s))` : 'aucune' },
                    ]}
                  />
                  <ul className={styles.texte}>
                    {resumeCharge(o).map((l) => (
                      <li key={l}>{l}</li>
                    ))}
                  </ul>
                </section>
                <section aria-label="Version du serveur" className={styles.bloc}>
                  <span className={styles.blocEtiquette}>Version du serveur</span>
                  <Definitions
                    elements={[
                      { terme: 'Changement sur le serveur', valeur: o.resultat?.motif ?? 'Motif non transmis' },
                      { terme: 'Version du serveur', valeur: <Mono>{o.resultat?.version ?? '?'}</Mono> },
                      { terme: 'Conflit constaté le', valeur: dateHeure(o.synchronisee_le) },
                    ]}
                  />
                </section>
                {o.objet_type === 'emplacement' ? <>
                  <p className={styles.aide}>Rien n&apos;a été écrasé. Rechargez la fiche et vérifiez la position et l&apos;occupant avant une nouvelle correction. Cette tentative reste conservée.</p>
                  <LienBouton href={`/terrain/places/${o.objet_id}`}>Vérifier la place</LienBouton>
                </> : <p className={styles.aide}>Rien n&apos;a été écrasé : le responsable technique retiendra ou écartera votre version depuis l&apos;espace Administration.</p>}
              </article>
            );
          })}
        </div>
      )}
      {rejets.length ? (
        <section className={styles.pile} aria-label="Opérations rejetées">
          <h2 className={styles.titreSection}>Opérations rejetées par le serveur</h2>
          {rejets.map((o) => (
            <p key={o.id_client} className={styles.aide}>
              {o.titre} · {o.objet_libelle} : {o.resultat?.motif ?? 'sans motif'} ({dateHeure(o.synchronisee_le)})
            </p>
          ))}
        </section>
      ) : null}
    </>
  );
}
